import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CardHeader,
  CardContent,
  Divider,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

import { useParams } from 'react-router-dom';
import { DetailPageHeader } from '../../components/layout/DetailPageHeader';
import { Add, Cancel, Engineering, Description } from '@mui/icons-material';
import { capitalizeWords, formatDate } from '../../utils/helper';
import {
  GETConsultancyRequestById,
  UpdateConsultancyStatus,
} from '../../services/consultancy';
import { GETQuotesForEnquiry } from '../../services/requests';
import { CustomerRequestDetails, QuotationsCard } from '../Amc/AMCRequest';
import AssignTechnicianModal from '../Booking/actions/TechnicianModal';
import AddQuotation from '../Amc/AddQuotation';
import useSnackbar from '../../hooks/useSnackbar';

export interface ConsultancyDetail {
  _id: string;
  consultancyId: string;
  enquiryId: string | null;
  status: string;
  enquiryStatus?: string;
  cancelReason?: string;
  slot: string | null;
  date: string | null;
  quantity: string;
  comment: string;
  place: string;
  alternatePhone: string;
  /** @deprecated use `photos` — kept for records saved before multi-photo support */
  documentURL: string;
  photos: string[];
  serviceName: any[];
  createdAt: string;
  updatedAt: string;

  addressDetails: {
    house?: string;
    street?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    saveAs?: string;
    landmark?: string;
  };

  brand: {
    _id?: string;
    name?: string;
  };

  userDetails: {
    _id: string;
    name: string;
    phoneNumber: string;
    countryCode: string;
    type?: string;
  };

  assignedTechnician: {
    _id: string;
    name: string;
    phoneNumber: string;
    email: string;
    countryCode: string;
    profilePhoto: string;
  };
}

const composeAddress = (
  address?: ConsultancyDetail['addressDetails'],
): string => {
  if (!address) return 'N/A';
  const parts = [
    address.house,
    address.street,
    address.landmark,
    address.city,
    address.state,
    address.zipcode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'N/A';
};

const getServiceDisplayName = (service: any): string => {
  if (typeof service === 'string') return service.trim();
  if (!service || typeof service !== 'object') return '';

  return String(
    service.name ??
      service.serviceName ??
      service.title ??
      service.label ??
      '',
  ).trim();
};

const isImageAttachment = (url: string): boolean => {
  const path = url.split('?')[0].split('#')[0];
  return /\.(png|jpe?g|webp|gif|bmp|avif)$/i.test(path);
};

const ConsultancyRequest = () => {
  const { id } = useParams();
  const [request, setRequest] = useState<ConsultancyDetail | null>(null);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openQuoteModal, setOpenQuoteModal] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  useEffect(() => {
    if (id) {
      fetchRequest(id);
    }
  }, [id]);

  useEffect(() => {
    const enquiryId = request?.enquiryId;
    if (!enquiryId) {
      setQuotes([]);
      return;
    }
    GETQuotesForEnquiry(enquiryId).then(setQuotes);
  }, [request?.enquiryId]);

  async function fetchRequest(consultancyId: string) {
    try {
      setLoading(true);
      setError('');

      const data = await GETConsultancyRequestById(consultancyId);

      setRequest(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load request');
    } finally {
      setLoading(false);
    }
  }

  const handleRejectRequest = async () => {
    if (!request?._id) return;

    try {
      setRejecting(true);
      await UpdateConsultancyStatus(request._id, 'CANCELLED', rejectReason.trim());
      showSnackbar('Consultancy request rejected', 'success');
      setOpenRejectDialog(false);
      setRejectReason('');
      if (id) {
        fetchRequest(id);
      }
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to reject request',
        'error',
      );
    } finally {
      setRejecting(false);
    }
  };

  const customer = useMemo(() => {
    if (!request) return null;

    return {
      _id: request.userDetails?._id ?? '',

      fullName: request.userDetails?.name ?? '',

      contactNumber: request.userDetails?.phoneNumber ?? '',

      customerType: request.userDetails?.type || 'N/A',

      placeType: request.place || 'N/A',

      address: composeAddress(request.addressDetails),

      totalAC: request.quantity || 'N/A',

      inspectionDate: formatDate(request.date),

      inspectionTime: request.slot || 'N/A',

      inspectionAddress: composeAddress(request.addressDetails),

      inspector: request.assignedTechnician?.name || '',

      cancelReason: request.cancelReason || '',
    };
  }, [request]);

  // The Consultancy document's own `status` only changes on explicit
  // reject/complete/close actions. Assigning a technician instead updates the
  // linked Enquiry's status — prefer that so the screen reflects assignment
  // immediately (previously showed "BOOKED" forever after assigning).
  const displayStatus = request?.enquiryStatus || request?.status || '';
  const isCancelled = request?.status === 'CANCELLED';

  if (loading) {
    return (
      <>
        <DetailPageHeader title={'Request Details'} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress sx={{ color: '#E31E25' }} />
        </Box>
      </>
    );
  }

  if (error && !request) {
    return (
      <>
        <DetailPageHeader title={'Request Details'} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            minHeight: '50vh',
          }}
        >
          <Typography color="error">{error}</Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => id && fetchRequest(id)}
          >
            Retry
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <DetailPageHeader title={'Request Details'} />
      <Box
        sx={{
          backgroundColor: '#1C1C1C',
          color: '#fff',
          borderRadius: 2,
          m: 3,
        }}
      >
        <CustomerRequestDetails
          customer={customer}
          status={capitalizeWords(displayStatus)}
          type="REGULAR"
        />

        <ConsultancyDetailsCard request={request} />

        <QuotationsCard quotes={quotes} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          m: 3,
        }}
      >
        {/* Left Button */}
        <Button
          variant="outlined"
          sx={{
            color: '#D32F2F',
            borderColor: '#D32F2F',
            px: 2.5,
            py: 1.2,
            borderRadius: '14px',
            '&:hover': {
              borderColor: '#B71C1C',
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
            },
          }}
          startIcon={<Cancel />}
          disabled={!request?._id || isCancelled}
          onClick={() => setOpenRejectDialog(true)}
        >
          Reject Request
        </Button>

        {/* Right Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<Add />}
            sx={{
              color: '#fff',
              backgroundColor: '#E31E25',
              '&:hover': {
                backgroundColor: '#C62828',
              },
              padding: '10px 20px',
              borderRadius: '14px',
            }}
            disabled={!request}
            onClick={() => setOpenQuoteModal(true)}
          >
            Add Quotation
          </Button>

          <Button
            startIcon={<Engineering />}
            sx={{
              color: '#fff',
              backgroundColor: '#E31E25',
              '&:hover': {
                backgroundColor: '#C62828',
              },
              padding: '10px 20px',
              borderRadius: '14px',
            }}
            disabled={!request?.enquiryId}
            onClick={() => setOpenAssignModal(true)}
          >
            {request?.assignedTechnician?.name
              ? 'Reassign Inspector'
              : 'Assign Inspector'}
          </Button>
        </Box>
      </Box>

      {/* Reject confirmation */}
      <Dialog
        open={openRejectDialog}
        onClose={() => !rejecting && setOpenRejectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reject Consultancy Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to reject this consultancy request? This
            will mark it as CANCELLED.
          </Typography>
          <TextField
            label="Cancellation reason"
            placeholder="Let the team know why this request is being rejected"
            fullWidth
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenRejectDialog(false)}
            disabled={rejecting}
          >
            Keep Request
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRejectRequest}
            disabled={rejecting}
          >
            {rejecting ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign inspector (via the linked enquiry) */}
      {request?.enquiryId && (
        <AssignTechnicianModal
          isOpen={openAssignModal}
          onClose={() => setOpenAssignModal(false)}
          bookingId={request.enquiryId}
          isEnquiry={true}
          onReload={() => id && fetchRequest(id)}
        />
      )}

      <AddQuotation
        open={openQuoteModal}
        onClose={() => setOpenQuoteModal(false)}
        id={id || ''}
        source="FREE_CONSULTATION"
        enquiryId={request?.enquiryId || undefined}
        onSaved={() => {
          if (id) fetchRequest(id);
          if (request?.enquiryId) GETQuotesForEnquiry(request.enquiryId).then(setQuotes);
        }}
      />

      {SnackbarComponent}
    </>
  );
};

export default ConsultancyRequest;

/* Figma-style details section: label rows on dark bands */
const ConsultancyDetailsCard = ({
  request,
}: {
  request: ConsultancyDetail | null;
}) => {
  const services = (request?.serviceName ?? [])
    .map(getServiceDisplayName)
    .filter(Boolean);

  const infoRows: { label: string; value: string; green?: boolean }[] = [
    {
      label: 'Consultancy ID',
      value: request?.consultancyId || 'N/A',
      green: true,
    },
    { label: 'Brand', value: request?.brand?.name || 'N/A' },
    {
      label: 'Services',
      value: services.length > 0 ? services.join(', ') : 'N/A',
    },
    { label: 'Quantity', value: request?.quantity || 'N/A' },
    // "Place Type" is already shown once above in the shared customer detail
    // card — repeating it here as "Place" made it appear twice on screen.
    { label: 'Alternate Phone', value: request?.alternatePhone || 'N/A' },
  ];

  return (
    <Box sx={{ color: '#fff' }}>
      <CardHeader
        title="Consultancy Details"
        titleTypographyProps={{
          fontWeight: 600,
          fontSize: 18,
        }}
      />

      <Divider sx={{ borderColor: '#565656' }} />

      <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {infoRows.map((row) => (
            <Paper
              key={row.label}
              elevation={0}
              sx={{
                backgroundColor: '#2E2E2E',
                borderRadius: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#9e9e9e',
                  width: '20%',
                  minWidth: 140,
                }}
              >
                {row.label}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: row.green ? '#0BE600' : '#FFFFFF',
                  fontWeight: row.green ? 600 : 400,
                }}
              >
                {row.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        {(() => {
          // Prefer the new `photos` array; fall back to the legacy single
          // `documentURL` for records saved before multi-photo support.
          const attachments =
            request?.photos && request.photos.length > 0
              ? request.photos
              : request?.documentURL
                ? [request.documentURL]
                : [];

          if (attachments.length === 0) return null;

          return (
            <>
              <Divider sx={{ my: 3, borderColor: '#565656' }} />

              <Typography variant="subtitle2" sx={{ mb: 1, color: '#9e9e9e' }}>
                Photos / Document
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {attachments.map((url, index) =>
                  isImageAttachment(url) ? (
                    <Box
                      key={`${url}-${index}`}
                      component="img"
                      src={url}
                      alt={`Consultancy attachment ${index + 1}`}
                      onClick={() => window.open(url, '_blank')}
                      sx={{
                        width: 140,
                        height: 140,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid #3a3a3a',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <Button
                      key={`${url}-${index}`}
                      startIcon={<Description />}
                      variant="outlined"
                      sx={{
                        color: '#E31E25',
                        borderColor: '#E31E25',
                        borderRadius: '10px',
                        '&:hover': {
                          borderColor: '#B71C1C',
                          backgroundColor: 'rgba(227, 30, 37, 0.08)',
                        },
                      }}
                      onClick={() => window.open(url, '_blank')}
                    >
                      View Attachment {attachments.length > 1 ? index + 1 : ''}
                    </Button>
                  ),
                )}
              </Box>
            </>
          );
        })()}

        {request?.comment && (
          <>
            <Divider sx={{ my: 3, borderColor: '#565656' }} />

            <Typography variant="subtitle2" sx={{ mb: 1, color: '#9e9e9e' }}>
              Customer Comment
            </Typography>

            <Typography variant="body2" sx={{ color: '#ddd' }}>
              {request.comment}
            </Typography>
          </>
        )}
      </CardContent>

      <Divider sx={{ borderColor: '#565656' }} />
    </Box>
  );
};



